// ========== 通用类型 ==========
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  code?: number;
}

export interface User {
  id: number;
  union_id?: string;
  name: string;
  role: string;
  username: string;
  card_type?: string;
  name_real?: string;
  id_card?: string;
  phone?: string;
  bank_card?: string;
  personal_bank_name?: string;
  bank_name?: string;
  company_name?: string;
  email?: string;
  bound_broker_id?: number;
  bound_broker_name?: string;
  role_id?: number;
}

// ========== 仪表盘 ==========
export interface DashboardStats {
  totalBrokers: number;
  totalFormulas: number;
  totalUsers: number;
  totalImports: number;
  totalSettlements: number;
  pendingConfirmations: number;
  monthlyRevenue: number;
}

export interface MonthlyTrend {
  month: string;
  count: number;
  revenue: number;
}

export interface PendingTask {
  id: number;
  type: string;
  description: string;
  createdAt: string;
}

// ========== 经纪人 ==========
export interface Broker {
  id: number;
  name: string;
  uid?: string;
  type: string;
  status: string;
  boundUserId?: number;
  boundUserName?: string;
  formulaId?: number;
  formulaName?: string;
  templateId?: number;
  templateName?: string;
  recruitBrokerId?: number;
  recruitBrokerName?: string;
  notes?: string;
}

export interface BrokerStats {
  total: number;
  active: number;
  inactive: number;
  unbound: number;
}

// ========== 公式 ==========
export interface Formula {
  id: number;
  name: string;
  expression: string;
  variables?: string;
  description?: string;
  status: string;
}

export interface FormulaCategory {
  value: string;
  label: string;
}

// ========== 结算 ==========
export interface SettlementMonth {
  month: string;
  count: number;
}

export interface SettlementData {
  id: number;
  import_id: number;
  settlement_month: string;
  anchor_name: string;
  broker_name: string;
  recruit_broker_name: string;
  total_stream: string;
  [key: string]: any;
}

export interface SettlementResult {
  id: number;
  broker_id: number;
  broker_name: string;
  settlement_month: string;
  formula_id: number;
  formula_name: string;
  total_amount: number;
  share_amount: number;
  details: Record<string, number>;
  created_at: string;
}

export interface SettlementSummary {
  broker_id: number;
  broker_name: string;
  total_amount: number;
  share_amount: number;
  count: number;
  formula_name?: string;
  formula_expression?: string;
  anchor_name?: string;
}

// ========== 导入 ==========
export interface ImportRecord {
  id: number;
  file_name: string;
  file_size: number;
  row_count: number;
  status: string;
  created_at: string;
  processed_at?: string;
  error_message?: string;
}

// ========== 账单模板 ==========
export interface ReportTemplate {
  id: number;
  name: string;
  type: string;
  status: string;
  config?: Record<string, any>;
}

// ========== 角色管理 ==========
export interface Role {
  id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  /** 权限配置数组（JSON解析后的PermissionConfig[]） */
  permissions?: PermissionConfig[];
}

export interface RolePermission {
  id: number;
  role_id: number;
  page: string;
  can_view: number;
  can_create: number;
  can_edit: number;
  can_delete: number;
}

export interface PermissionConfig {
  page: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface UserPermissions {
  [page: string]: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
  };
}

// ========== 账单管理 ==========
export interface Bill {
  id: number;
  brokerId: number;
  brokerName: string;
  brokerRealName?: string;
  brokerPhone?: string;
  brokerIdCard?: string;
  settlementMonth: string;
  shareAmount: number;
  invoiceUrl?: string;
  invoiceFileType?: string;
  status: 'pending' | 'confirmed' | 'paid';
  operatorId?: number;
  operatorName?: string;
  operatedAt?: string;
  createdAt: string;
  updatedAt: string;
  // 确认金额相关字段
  confirmedAt?: string;
  confirmOperator?: string;
  // 付款相关字段
  paidAt?: string;
  payOperator?: string;
  // 驳回相关字段
  rejectReason?: string;
  // 工资申报相关字段
  wageDeclared?: boolean;
  wageDeclaredAt?: string;
  wageDeclaredBy?: string;
}

// 账单聚合数据接口
export interface BillAggregatedData {
  formula: Formula | null;
  fieldValues: Record<string, number>;
  shareDetails?: Record<string, number>;
  totalAmount: number;
  allFields?: string[];
}

export interface BillFilter {
  brokerId?: number;
  month?: string;
  confirmStatus?: string;
  paymentStatus?: string;
}

export interface BillStats {
  total: number;
  pending: number;
  confirmed: number;
  paid: number;
}

// ========== 字段定义（从Excel导入） ==========
export interface FieldDefinition {
  /** 字段唯一标识（英文key，用于公式引用） */
  key: string;
  /** 中文标签（Excel列名） */
  label: string;
  /** 字段分类 */
  category: string;
  /** 数据类型 */
  dataType: 'number' | 'string' | 'boolean' | 'date';
}

/** 导入的字段映射存储 */
export interface ImportedFields {
  /** 关联的导入记录ID */
  importId: number;
  /** 文件名 */
  fileName: string;
  /** 导入时间 */
  importedAt: string;
  /** 字段列表 */
  fields: FieldDefinition[];
}

// ========== 菜单 ==========
export interface MenuItem {
  path: string;
  label: string;
  icon: string;
  adminOnly: boolean;
}

export const MENU_ITEMS: MenuItem[] = [
  { path: '/report', label: '结算报表', icon: 'BarChart3', adminOnly: false },
  { path: '/wage-declaration', label: '工资提报', icon: 'FileText', adminOnly: false },
  { path: '/brokers', label: '经纪人管理', icon: 'Users', adminOnly: false },
  { path: '/formulas', label: '公式配置', icon: 'Calculator', adminOnly: false },
  { path: '/import', label: '数据导入', icon: 'Upload', adminOnly: false },
  { path: '/report-template', label: '账单模板', icon: 'FileText', adminOnly: false },
  { path: '/users', label: '用户管理', icon: 'UserCog', adminOnly: false },
  { path: '/roles', label: '角色管理', icon: 'Shield', adminOnly: false },
  { path: '/bills', label: '账单管理', icon: 'Receipt', adminOnly: false },
];

export const PAGE_MAP: Record<string, string> = {
  brokers: '经纪人管理',
  formulas: '公式配置',
  report: '结算报表',
  'report-template': '账单模板',
  import: '数据导入',
  users: '用户管理',
  roles: '角色管理',
  bills: '账单管理',
  'wage-declaration': '工资提报',
};
